import { BehaviorContext } from './behavior-context';
import { Injectable } from '@angular/core';

export abstract class Behavior {
  abstract execute(
    args: { [key: string]: any },
    context: BehaviorContext
  ): Promise<any>;
}

export abstract class ConditionalBehavior {
  abstract execute(
    args: { [key: string]: any },
    context: BehaviorContext
  ): Promise<boolean>;
}

const PROP_ARGS = 'ARGS';
const PROP_GLOBAL_INPUT = 'GLOBAL_INPUT_PROPS';
const PROP_GLOBAL_OUTPUT = 'GLOBAL_OUTPUT_PROPS';

/* 
STATIC PROPS and what they do

  // Describe what the function is for, this is used for the CMS editor tool
  static readonly DESCRIPTION = 'The behavior to save the application';

  // Define a set of output props that must be defined by the end of the function running
  static readonly GLOBAL_OUTPUT_PROPS = {
    error: {
      type: 'Error',
      name: 'error',
      friendlyName: 'Error',
      description: 'An object containing an error',
    },
    fundingApplicationId: {
      name: GLOBAL_KEY_APPROVE_ME_FUNDING_APP_ID,
      type: 'number',
      description: 'The returned funding application id',
    },
  };

  // Define a set of input props that must be defined before the function runs
  static readonly GLOBAL_INPUT_PROPS = {
    fundingApplicationId: {
      required: false,
      name: GLOBAL_KEY_APPROVE_ME_FUNDING_APP_ID,
      type: 'number',
      description: 'The current funding application id',
    },
  };

  // Internal version tracker of the function
  static readonly VERSION = '1.0.0';

  // The args definition
  static readonly ARGS = {
    url: {
      required: true,
      name: 'url',
      type: 'string',
      description: 'The Approve.Me Orchestrator URL',
    },
  };


*/
@Injectable()
export abstract class StrictBehavior {
  name: string;
  constructor() {
    this.name = this.constructor.name;
    if (!this.name) {
      throw new Error('Constructor is not named in StrictBehavior.');
    }
  }

  async execute(args: any, ctx: BehaviorContext) {
    // Step 1: Check that the args object is appropriately populated.
    const argsDef = Reflect.get(this.constructor, PROP_ARGS);
    if (argsDef) {
      Object.keys(argsDef).forEach(argDefKey => {
        const def = argsDef[argDefKey];
        if (def.required && (!args || !args[argDefKey])) {
          throw Error(
            `Argument missing: ${argDefKey} in behavior: ${this.name}`
          );
        }
        // TODO: check types as well?
      });
    }

    // Step 2: Check that the global context is appropriately populated
    let globalDef = Reflect.get(this.constructor, PROP_GLOBAL_INPUT);
    this.checkObjectAgainstDefinition(globalDef, ctx);

    // Execute the behavior
    await this.executeBehavior(args, ctx);

    // Step 3: Check that the global context was properly set
    globalDef = Reflect.get(this.constructor, PROP_GLOBAL_OUTPUT);
    this.checkObjectAgainstDefinition(globalDef, ctx, 'output');
  }

  private checkObjectAgainstDefinition(def, ctx, type = 'input') {
    if (def) {
      Object.keys(def).forEach(defKey => {
        const iDef = def[defKey];
        if (iDef.required && !ctx.get(defKey)) {
          throw Error(
            `Global ${type} property missing: ${defKey} in behavior: ${this.name}`
          );
        }
      });
    }
  }

  abstract executeBehavior(args: any, ctx: BehaviorContext): Promise<any>;
}
